import React, { CSSProperties, useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls } from "@react-three/drei";
import { useAtom } from "jotai/index";

// ルートパスからのインポート
import Model from "~/Model";
import OutputText from "~/voice_utils/TextOutput";
import RubyText from "~/components/utils/RubyText";
import ThoughtBubble from "~/components/animation/ThoughtBubble";
import { getVRMGlobalName, getVRMName } from "~/vrm_utils/VRMUtils";
import {
  characterListVersionAtom,
  currentVRMAtom,
  languageAtom,
  volumeAtom,
  isListeningAtom,
  isFaceDetectionAtom,
  vrmListAtom,
  vrmPathAtom,
  T2SEngineAtom,
  standbyModeAtom,
  layoutSelectAtom,
  model3dLoadedAtom,
} from "~/Atom";
import * as db from "~/components/db/CharacterDBUtils";
import { Character } from "shared/Schemas";
import { t2SEngines, currentLocale, talkModes } from "~/Commons";
import { PlaySound } from "~/SoundUtils";
import FaceDetection from "~/components/FaceDetection";
import * as icon from "~/Icons";
import ModelViewLayout1 from "~/components/layout/ModelViewLayout1";
import ModelViewLayout2 from "~/components/layout/ModelViewLayout2";
import ModelViewLayout3 from "~/components/layout/ModelViewLayout3";

const canvasStyle: CSSProperties = {
  borderRadius: "10px",
  display: "block",
  height: "60% !important",
};

// カメラ設定オブジェクト
const cameraSettings = {
  fov: 29.5, // 視野角（度）- カメラの視野の広さを表す
  near: 0.1, // 近クリップ面距離 - これより近いオブジェクトは描画されない
  far: 100, // 遠クリップ面距離 - これより遠いオブジェクトは描画されない
  position: {
    x: 0, // カメラのX座標 - 横方向の位置
    y: -0.005, // カメラのY座標 - 縦方向の位置
    z: 2.0, // カメラのZ座標 - モデルからの距離（奥行き）
  },
};

// ライト設定オブジェクト
const lightSettings = {
  directional: {
    position: {
      x: 0, // 平行光源のX座標（左右位置）
      y: 10, // 平行光源のY座標（高さ）
      z: 10, // 平行光源のZ座標（奥行き）
    },
    color: 0xffffff, // 平行光源の色（白色）
    intensity: 3.0, // 平行光源の強度
  },
  ambient: {
    color: 0xffffff, // 環境光の色（白色）
    intensity: 0.5, // 環境光の強度
  },
};

// レンダラー設定オブジェクト
const glConfig = {
  antialias: true,
  alpha: true,
  outputColorSpace: THREE.SRGBColorSpace,
  toneMapping: THREE.LinearToneMapping,
  toneMappingExposure: 1.0,
};

// ボリュームと言語設定の定数
const volumeOn = 1;
const volumeOff = 0;

interface ModelViewProps {
  visible: boolean;
}

/**
 * 3Dモデルと関連UIを表示するコンポーネント
 */
const ModelView: React.FC<ModelViewProps> = ({ visible }) => {
  const [volume, setVolume] = useAtom(volumeAtom);
  const [, setIsModelViewed] = useState(false);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [characterListVersion] = useAtom(characterListVersionAtom);
  const [vrmList] = useAtom(vrmListAtom);
  const [, setVRMPath] = useAtom(vrmPathAtom);
  const [name, setName] = useState<string>("");
  const [characterIndex, setCharacterIndex] = useState<number>(0);
  const [maxIndex, setMaxIndex] = useState<number>(0);
  const [language] = useAtom(languageAtom);
  const [isListening] = useAtom(isListeningAtom);
  const [isFaceDetection] = useAtom(isFaceDetectionAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const [standbyMode] = useAtom(standbyModeAtom);
  const [layoutSelect] = useAtom(layoutSelectAtom);
  const [isModel3dLoaded] = useAtom(model3dLoadedAtom);
  // 可変項目のみカメラ設定をstate化
  const [cameraFov, setCameraFov] = useState(cameraSettings.fov);
  const [cameraPositionY, setCameraPositionY] = useState(cameraSettings.position.y);
  const [canvasKey, setCanvasKey] = useState(0); // Canvas再マウント用

  /**
   * ビューポートが縦向きの時にモデルのカメラ設定(fov/Y位置)を調整する
   */
  useEffect(() => {
    // 初期値を保存
    const originalFov = cameraSettings.fov;
    const originalPositionY = cameraSettings.position.y;

    // リサイズ時にfovとY位置を更新
    const updateFovAndPosition = () => {
      if (typeof window === "undefined") return;
      const isPortrait = window.innerHeight > window.innerWidth;
      const portraitCSFov = 21; // 縦向き時のfov値
      const portraitCSPositionY = -0.05; // 縦向き時のカメラY位置
      if (isPortrait) {
        setCameraFov(portraitCSFov);
        setCameraPositionY(portraitCSPositionY);
      } else {
        setCameraFov(originalFov);
        setCameraPositionY(originalPositionY);
      }
      // Canvasを再マウントしてcameraプロパティを反映させる
      setCanvasKey((prev) => prev + 1);
    };

    updateFovAndPosition();
    window.addEventListener("resize", updateFovAndPosition);
    return () => window.removeEventListener("resize", updateFovAndPosition);
  }, []);

  /**
   * 現在のキャラクターのインデックスを取得する
   */
  const getCurrentIndex = async (): Promise<void> => {
    // currentVRMが空の場合、3Dモデル読み込みが未完了の場合は処理をスキップする
    if (!currentVRM || !isModel3dLoaded) return;

    // 今のキャラクター名を取得
    const currentCharacter = getVRMName(currentVRM);

    // 有効モデルに対応するキャラクターだけを対象にする
    const characters = await db.selectAvailableCharacters(vrmList);
    setMaxIndex(characters.length);

    // currentCharacterと一致するキャラクターの番号を取得
    const currentIndex = characters.findIndex(
      (character: Character) => character.name === currentCharacter
    );

    if (currentIndex >= 0) {
      setCharacterIndex(currentIndex);
    } else {
      setCharacterIndex(0);
    }
  };

  useEffect(() => {
    getCurrentIndex();

    const fetchCharacterName = async (): Promise<void> => {
      let cachedName = getVRMName(currentVRM);

      if (language !== currentLocale) {
        cachedName = await getVRMGlobalName(currentVRM);
      }

      setName(cachedName);
    };

    // 非同期関数を呼び出し
    fetchCharacterName();
  }, [currentVRM, language, vrmList, characterListVersion, isModel3dLoaded]);

  /**
   * モデルの表示完了時の処理
   */
  const handleModelViewed = (): void => {
    setIsModelViewed(true);
  };

  const toggleMute = (): void => {
    if (volume === volumeOff) {
      setVolume(volumeOn);
      PlaySound("on");
    } else {
      PlaySound("off");
      setVolume(volumeOff);
    }
  };

  /**
   * キャラクター変更の共通ロジック
   *
   * @param step - 変更ステップ（+1: 次へ, -1: 前へ）
   * @returns
   */
  const changeCharacter = async (step: number) => {
    // 今のキャラクター名を取得
    const currentCharacter = getVRMName(currentVRM);
    const characters = await db.selectAvailableCharacters(vrmList);
    if (characters.length === 0) return;
    // currentCharacterと一致するキャラクターの番号を取得
    let currentIndex = characters.findIndex((character) => character.name === currentCharacter);
    // currentIndexが見つからない場合は0に設定
    if (currentIndex < 0) currentIndex = 0;

    // 対象のインデックスを計算
    const targetIndex = (currentIndex + step + characters.length) % characters.length;
    const targetCharacter = characters[targetIndex];
    const vrmData = vrmList.find((vrm) => vrm.name === `${targetCharacter.global_name}.vrm`);
    if (!vrmData) return;

    setCharacterIndex(targetIndex);
    setVRMPath(vrmData.url);
    // ttsEngine設定
    if (language === currentLocale) {
      const ttsEngine = targetCharacter.tts_engine;
      setT2SEngine(ttsEngine);
    } else {
      setT2SEngine(t2SEngines.openai);
    }

    // キャラクター切り替え後のT2Sエンジン設定
    PlaySound("push", volume);
  };

  const isSoundOn = volume === volumeOn;
  const changeButtonsDisabled = !isListening && standbyMode !== talkModes.stopped;
  const setNextCharacter = () => changeCharacter(1);
  const setPrevCharacter = () => changeCharacter(-1);
  switch (layoutSelect) {
    case 1:
      return (
        <ModelViewLayout1
          visible={visible}
          handleModelViewed={handleModelViewed}
          name={name}
          characterIndex={characterIndex}
          maxIndex={maxIndex}
          isSoundOn={isSoundOn}
          toggleMute={toggleMute}
          isFaceDetection={isFaceDetection}
          setPrevCharacter={setPrevCharacter}
          setNextCharacter={setNextCharacter}
          changeButtonsDisabled={changeButtonsDisabled}
        />
      );
    case 2:
      return (
        <ModelViewLayout2
          visible={visible}
          handleModelViewed={handleModelViewed}
          name={name}
          isSoundOn={isSoundOn}
          toggleMute={toggleMute}
          isFaceDetection={isFaceDetection}
          setPrevCharacter={setPrevCharacter}
          setNextCharacter={setNextCharacter}
          changeButtonsDisabled={changeButtonsDisabled}
        />
      );
    case 3:
      return (
        <ModelViewLayout3
          visible={visible}
          handleModelViewed={handleModelViewed}
          name={name}
          characterIndex={characterIndex}
          maxIndex={maxIndex}
          isSoundOn={isSoundOn}
          toggleMute={toggleMute}
          isFaceDetection={isFaceDetection}
          setPrevCharacter={setPrevCharacter}
          setNextCharacter={setNextCharacter}
          changeButtonsDisabled={changeButtonsDisabled}
        />
      );

    default:
      return (
        <div className="character-area">
          <div className="character-name">
            <RubyText text={name} />
            <div className="indicator vertical-only">
              <span>{characterIndex + 1}</span>/{maxIndex}
            </div>
          </div>

          <OutputText />

          <div className="chatpage-canvas">
            <Canvas
              key={canvasKey}
              className={`main-canvas ${visible ? "visible" : "hide"}`}
              // カメラ設定の使用箇所
              camera={{
                fov: cameraFov,
                near: cameraSettings.near,
                far: cameraSettings.far,
                position: [cameraSettings.position.x, cameraPositionY, cameraSettings.position.z],
              }}
              flat
              style={canvasStyle}
              gl={glConfig}
            >
              {/* ライト設定の使用箇所 */}
              {/* eslint-disable react/no-unknown-property */}
              <directionalLight
                position-x={lightSettings.directional.position.x}
                position-y={lightSettings.directional.position.y}
                position-z={lightSettings.directional.position.z}
                color={lightSettings.directional.color}
                intensity={lightSettings.directional.intensity}
              />
              <OrbitControls enableZoom={false} enableRotate={false} enablePan={false} />
              <ambientLight
                color={lightSettings.ambient.color}
                intensity={lightSettings.ambient.intensity}
              />
              <Model onViewed={handleModelViewed} />
            </Canvas>
            <ThoughtBubble />
          </div>
          <div className="volume-btn">
            <button
              className={`btn btn-circle ${isSoundOn ? "sound-on" : "sound-off"}`}
              onClick={toggleMute}
            >
              {isSoundOn ? (
                <span className="material-symbols-rounded sound-on-btn">volume_up</span>
              ) : (
                <span className="material-symbols-rounded sound-off-btn">volume_off</span>
              )}
            </button>
          </div>

          {/*効率化のためisFaceDetectionがOFFなら切る*/}
          {isFaceDetection && <FaceDetection />}
          <div className="character-button">
            <div className="change-btn">
              <button
                className="btn btn-circle"
                onMouseUp={() => {
                  setPrevCharacter().catch((error) =>
                    console.error("Error in setPrevCharacter:", error)
                  );
                }}
                disabled={changeButtonsDisabled}
              >
                <icon.icons_arrow_left />
              </button>
              <div className="indicator horizontal-only">
                <span>{characterIndex + 1}</span>/{maxIndex}
                {/*
                「・・」の場合は、こちらを利用
                <Indicator characterIndex={characterIndex} maxIndex={maxIndex} />
              */}
              </div>
              <button
                className="btn btn-circle"
                onMouseUp={() => {
                  setNextCharacter().catch((error) =>
                    console.error("Error in setNextCharacter:", error)
                  );
                }}
                disabled={changeButtonsDisabled}
              >
                <icon.icons_arrow_right />
              </button>
            </div>
          </div>
        </div>
      );
  }
};

export default ModelView;
